import React, {useState} from 'react'
import {useNavigate} from 'react-router-dom'
import {toast} from 'react-toastify'
import {saveConnectionTestResult} from './api'
import {useConnectionTest} from './useConnectionTest'
import {ConnectionTestResult} from './types'
import {ProviderMeasurement} from './providers'

type Phase = 'idle' | 'testing' | 'result'

const BRAND = 'rgb(163, 162, 66)'

const StabilityBadge: React.FC<{isStable: boolean}> = ({isStable}) =>
  isStable ? (
    <span
      className='badge fw-bold px-3 py-2'
      style={{backgroundColor: '#E7F4D9', color: '#6B8E23', borderRadius: 6}}
    >
      Good
    </span>
  ) : (
    <span
      className='badge fw-bold px-3 py-2'
      style={{backgroundColor: '#FDE2E4', color: '#C0392B', borderRadius: 6}}
    >
      Fix Internet Connection
    </span>
  )

const MetricCard: React.FC<{
  icon: string
  label: string
  value: number
  unit: string
}> = ({icon, label, value, unit}) => (
  <div
    className='p-4 flex-grow-1'
    style={{
      backgroundColor: '#F7F7F5',
      borderRadius: 10,
      minWidth: 140,
    }}
  >
    <div className='d-flex align-items-center text-gray-700 mb-2'>
      <i className={`${icon} me-2`} style={{fontSize: 16, color: BRAND}}></i>
      <span className='fs-7 fw-semibold'>{label}</span>
    </div>
    <div className='d-flex align-items-baseline'>
      <span className='fw-bolder text-dark' style={{fontSize: 28}}>{value}</span>
      <span className='text-gray-600 ms-2 fs-7'>{unit}</span>
    </div>
  </div>
)

const ConnectionTestPage: React.FC = () => {
  const navigate = useNavigate()
  const [phase, setPhase] = useState<Phase>('idle')
  const [result, setResult] = useState<ConnectionTestResult | null>(null)
  const [source, setSource] = useState<ProviderMeasurement['source'] | null>(null)
  const {runTest} = useConnectionTest()

  const handleRun = async () => {
    setPhase('testing')
    try {
      const measurement = await runTest()
      const {data} = await saveConnectionTestResult({
        latencyMs: measurement.latencyMs,
        jitterMs: measurement.jitterMs,
      })
      setSource(measurement.source)
      setResult(data)
      setPhase('result')
    } catch (err: any) {
      const message =
        err?.response?.data?.text ||
        err?.message ||
        'Unable to complete the connection test. Please try again.'
      toast.error(message)
      setPhase('idle')
    }
  }

  const handleClose = () => {
    navigate('/dashboard')
  }

  return (
    <>
      <div className='mb-5'>
        <div className='d-flex align-items-center'>
          <button
            className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-3'
            onClick={handleClose}
            aria-label='Back to dashboard'
          >
            <i className='bi bi-arrow-left-circle' style={{fontSize: 28}}></i>
          </button>
          <div>
            <h1 className='mb-0 fw-bold mb-2' style={{fontSize: 25, lineHeight: '32px'}}>
              Connection Test
            </h1>
            <p className='text-gray-500 fs-5 mb-0'>
              Measures the responsiveness and stability of your internet connection.
            </p>
          </div>
        </div>
      </div>

      <div className='card mb-5 shadow-sm' style={{borderRadius: 12}}>
        <div className='card-body p-10'>
          {phase === 'idle' && <IdleView onRun={handleRun} />}
          {phase === 'testing' && <TestingView />}
          {phase === 'result' && result && (
            <ResultView
              result={result}
              source={source}
              onClose={handleClose}
              onRetry={handleRun}
            />
          )}
        </div>
      </div>
    </>
  )
}

const IdleView: React.FC<{onRun: () => void}> = ({onRun}) => (
  <div className='text-center py-8'>
    <div
      className='d-inline-flex align-items-center justify-content-center rounded-circle mb-6'
      style={{width: 72, height: 72, backgroundColor: '#F4F4E4'}}
    >
      <i className='bi bi-wifi' style={{fontSize: 34, color: BRAND}}></i>
    </div>
    <h2 className='fw-bolder mb-3' style={{fontSize: 24}}>
      Connection Test
    </h2>
    <p className='text-gray-600 fs-5 mb-8' style={{maxWidth: 520, margin: '0 auto 32px'}}>
      Measures the responsiveness and stability of your internet connection.
    </p>

    <div
      className='mx-auto p-8'
      style={{
        maxWidth: 560,
        backgroundColor: '#F7F7F5',
        borderRadius: 12,
      }}
    >
      <div
        className='d-inline-flex align-items-center justify-content-center mb-4'
        style={{width: 44, height: 44, borderRadius: 8, backgroundColor: '#fff'}}
      >
        <i className='bi bi-hdd-stack' style={{fontSize: 20, color: BRAND}}></i>
      </div>
      <h3 className='fw-bold mb-2' style={{fontSize: 18}}>
        Ready to Connect
      </h3>
      <p className='text-gray-600 fs-6 mb-6'>
        Click the button below to initiate a sequence of pings. The test takes approximately 5-10
        seconds to complete.
      </p>
      <button
        type='button'
        className='btn w-100 fw-semibold d-inline-flex align-items-center justify-content-center'
        style={{backgroundColor: BRAND, color: '#fff', paddingTop: 12, paddingBottom: 12}}
        onClick={onRun}
      >
        <i className='bi bi-activity me-2' style={{fontSize: 16}}></i>
        Run Test
      </button>
    </div>
  </div>
)

const TestingView: React.FC = () => (
  <div className='text-center py-10'>
    <div
      className='d-inline-flex align-items-center justify-content-center mb-5'
      style={{width: 72, height: 72}}
    >
      <div
        className='spinner-border'
        style={{width: 56, height: 56, color: BRAND, borderWidth: 4}}
        role='status'
      >
        <span className='visually-hidden'>Testing...</span>
      </div>
    </div>
    <h3 className='fw-bolder mb-2' style={{fontSize: 20}}>
      Testing connection
    </h3>
    <p className='text-gray-600 fs-6 mb-0'>Please wait while we analyze your network quality...</p>
  </div>
)

const ResultView: React.FC<{
  result: ConnectionTestResult
  source: ProviderMeasurement['source'] | null
  onClose: () => void
  onRetry: () => void
}> = ({result, source, onClose, onRetry}) => {
  const verdictBg = result.isStable ? '#F1F8E4' : '#FDECEC'
  const verdictText = result.isStable ? 'Your connection is stable' : 'Your connection is unstable'
  const sourceLabel =
    source === 'cloudflare'
      ? 'Measured via Cloudflare global edge'
      : source === 'http-ping'
      ? 'Measured via server ping (fallback)'
      : null

  return (
    <div className='mx-auto' style={{maxWidth: 560}}>
      <div className='d-flex align-items-center justify-content-between mb-5'>
        <div className='d-flex align-items-center'>
          <div
            className='d-inline-flex align-items-center justify-content-center me-3'
            style={{width: 36, height: 36, borderRadius: 8, backgroundColor: '#F4F4E4'}}
          >
            <i className='bi bi-activity' style={{fontSize: 18, color: BRAND}}></i>
          </div>
          <h3 className='fw-bolder mb-0' style={{fontSize: 18}}>
            Test Results
          </h3>
        </div>
        <button
          type='button'
          className='btn btn-icon btn-sm btn-light'
          onClick={onClose}
          aria-label='Close'
        >
          <i className='bi bi-x-lg'></i>
        </button>
      </div>

      <div className='d-flex gap-3 mb-4 flex-wrap'>
        <MetricCard icon='bi bi-wifi' label='Latency' value={result.latencyMs} unit='ms' />
        <MetricCard icon='bi bi-activity' label='Jitter' value={result.jitterMs} unit='ms' />
      </div>

      <div
        className='p-4'
        style={{backgroundColor: verdictBg, borderRadius: 10}}
      >
        <div className='d-flex align-items-center mb-1'>
          {result.isStable ? (
            <i className='bi bi-check-circle me-2' style={{color: '#6B8E23', fontSize: 18}}></i>
          ) : (
            <i className='bi bi-exclamation-circle me-2' style={{color: '#C0392B', fontSize: 18}}></i>
          )}
          <span className='fw-bold me-3'>Verdict:</span>
          <StabilityBadge isStable={result.isStable} />
        </div>
        <div className='text-gray-700 fs-7 ms-7'>{verdictText}</div>
      </div>

      {sourceLabel && (
        <div className='text-gray-500 fs-8 mt-3 text-end'>
          <i className='bi bi-info-circle me-1'></i>
          {sourceLabel}
        </div>
      )}

      <div className='d-flex justify-content-end mt-6'>
        <button type='button' className='btn btn-light me-3 fw-semibold' onClick={onRetry}>
          Run Again
        </button>
        <button
          type='button'
          className='btn fw-semibold'
          style={{backgroundColor: BRAND, color: '#fff'}}
          onClick={onClose}
        >
          Go to Dashboard
        </button>
      </div>
    </div>
  )
}

export default ConnectionTestPage
